import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    setPassword,
    setConfirmPassword,
    useAppStateResetPasswordSelector,
    setExpiredUrl
} from './resetPasswordSlice'
import {
    resetPasswordRequest,
} from './resetPasswordActionCreators';
import styles from './ResetPassword.module.scss';
import { Input, Message } from 'semantic-ui-react';
import { NavLink } from 'react-router-dom';
import { firebaseService } from '../../services/firebaseService';
import { Reasoning, ErrorValidationLabel } from '../../components/errorValidationLabel/ErrorValidationLabel';
import { Button } from '../../components/button/Button';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../components/spinner/Spinner';

export function ResetPassword() {
    const {
        password,
        confirmPassword,
        expiredUrl,
        success,
        loading
    } = useAppStateResetPasswordSelector(state => state);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    let [invalidPassword, setInvalidPassword] = useState<Reasoning | null>(null);
    let [invalidConfirmPassword, setInvalidConfirmPassword] = useState<Reasoning | null>(null);

    const oobCode = new URLSearchParams(window.location.search).get('oobCode');
    const mode = new URLSearchParams(window.location.search).get('mode')?.toLowerCase();

    useEffect(() => {
        if (!oobCode) {
            dispatch(setExpiredUrl(true));
            return;
        }
        firebaseService.auth().verifyPasswordResetCode(oobCode)
        .then(() => {
            if (mode === 'verifyemail') {
                window.location.assign('/dashboard');
            } else {
                dispatch(setExpiredUrl(false));
            }
        }).catch(() => {
            dispatch(setExpiredUrl(true));
        });
    }, [dispatch, oobCode, mode]);

    let handleResetPassword = () => {
        let valid = validateForm();
        if (valid) {
            dispatch(resetPasswordRequest(oobCode!, password));
        }
    }

    let validateForm = () => {
        clearValidators();
        let validForm = true;

        const passwordRegex = new RegExp("^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{8,}$");

        if (password.length === 0) {
            setInvalidPassword('Required');
            validForm = false;
        } else if (password.match(passwordRegex) === null) {
            setInvalidPassword('RequirementsNotMet');
            validForm = false;
        }

        if (confirmPassword.length === 0) {
            setInvalidConfirmPassword('Required');
            validForm = false;
        } else if (confirmPassword !== password) {
            setInvalidConfirmPassword('Match');
            validForm = false;
        }

        return validForm;
    }

    let clearValidators = () => {
        setInvalidPassword(null);
    }

    if (expiredUrl) {
        return(
            <div className={styles.page}>
                <h1 className="visually-hidden">{t('Login.ResetPassword')}</h1>
                <Message negative>
                    <Message.Header>{t('Login.UrlHasExpired')}</Message.Header>
                </Message>
            </div>
        );
    }

    if (expiredUrl === null) {
        return (
            <div className={styles.page}>
                <div className='headerContainer'>
                    <h1>{t('Login.ResetPassword')}</h1>
                    <Spinner isLoading={true}/>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.form}>
                {success ?
                    <Message positive>
                        <Message.Header>{t('Login.PasswordResetSuccessfully')}</Message.Header>
                    </Message>
                : null}
                <div className={styles.row}>
                    <div className='headerContainer'>
                        <h1>{t('Login.ResetPassword')}</h1>
                        <Spinner isLoading={loading}/>
                    </div>
                </div>

                <div className={styles.row}>
                    <label htmlFor="reset-password">{t('Login.NewPassword')}</label>
                    <Input
                        id="reset-password"
                        value={password}
                        onChange={(e: any) => dispatch(setPassword({ value: e.target.value }))}
                        type="password"
                        autoComplete="new-password"
                        aria-describedby={invalidPassword ? 'reset-password-error' : undefined}
                        aria-invalid={!!invalidPassword}
                    />
                    {invalidPassword ?
                        <ErrorValidationLabel
                            fieldDescription={t('Login.NewPassword')}
                            reasoning={invalidPassword}
                            id="reset-password-error"
                            requirements={[
                                'ErrorValidation.MinimumEightCharacters',
                                'ErrorValidation.OneUpperCaseLetter',
                                'ErrorValidation.OneLowerCaseLetter',
                                'ErrorValidation.OneNumber',
                                'ErrorValidation.OneSpecialCharacter',
                            ]}
                        />
                    : null}
                </div>

                <div className={styles.row}>
                    <label htmlFor="reset-confirm-password">{t('Login.ConfirmPassword')}</label>
                    <Input
                        id="reset-confirm-password"
                        value={confirmPassword}
                        onChange={(e: any) => dispatch(setConfirmPassword({ value: e.target.value }))}
                        type="password"
                        autoComplete="new-password"
                        aria-describedby={invalidConfirmPassword ? 'reset-confirm-password-error' : undefined}
                        aria-invalid={!!invalidConfirmPassword}
                    />
                    {invalidConfirmPassword ? <ErrorValidationLabel fieldDescription={t('Login.ConfirmPassword')} reasoning={invalidConfirmPassword} id="reset-confirm-password-error" /> : null}
                </div>

                {!success ?
                    <div className={styles.row}>
                        <Button color='blue' onClick={handleResetPassword} >
                            {t('Login.ResetPassword')}
                        </Button>
                    </div>
                :
                    <div className={styles.row}>
                        <NavLink exact to="/login" className={styles.center}>{t('Login.ReturnToLogin')}</NavLink>
                    </div>
                }
            </div>
        </div>
    );
}